import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { buttonStyles } from '../../atoms/button/utils/button-styles';
import { cn } from '../../utils/cn';
export const NotFound = ({ title, body, renderCta, isRebrandPalette = false }) => {
    const animationContainer = React.useRef(null);
    // we use a mutable ref to avoid triggering re-renders
    const relativeXPosition = React.useRef(0);
    // get the current pointer X position as a number in [0, 1] range
    const getRelativePointerXPosition = (clientX) => 1 - Math.min(clientX, window.innerWidth) / window.innerWidth;
    // Track mouse X position and save it
    React.useEffect(() => {
        const callback = (event) => {
            relativeXPosition.current = getRelativePointerXPosition(event.clientX);
        };
        document.addEventListener('mousemove', callback, false);
        return () => document.removeEventListener('mousemove', callback);
    }, []);
    // Track touch X position (only on the image) and save it
    React.useEffect(() => {
        const callback = (event) => {
            if (event.touches.length === 1) {
                relativeXPosition.current = getRelativePointerXPosition(event.touches[0].clientX);
            }
        };
        const element = animationContainer.current;
        if (!element) {
            return;
        }
        element.addEventListener('touchmove', callback, false);
        return () => element.removeEventListener('touchmove', callback);
    }, []);
    const initialAngle = React.useRef(null);
    // Track device orientation and save it
    React.useEffect(() => {
        function handleOrientation(event) {
            const tiltAngle = event.alpha ?? 0;
            if (initialAngle.current === null) {
                initialAngle.current = tiltAngle;
            }
            const MAX_TILT_LEFT = 45;
            const MAX_TILT_RIGHT = -45;
            const clampedTilt = Math.min(Math.max(tiltAngle - initialAngle.current, MAX_TILT_RIGHT), MAX_TILT_LEFT);
            relativeXPosition.current =
                (clampedTilt - MAX_TILT_RIGHT) / (MAX_TILT_LEFT - MAX_TILT_RIGHT);
        }
        window.addEventListener('deviceorientation', handleOrientation, true);
        return () => window.removeEventListener('deviceorientation', handleOrientation, true);
    }, []);
    // Initialize the Lottie animation in a paused state, and save the reference to the animation
    const lottieAnimation = React.useRef(null);
    React.useEffect(() => {
        const container = animationContainer.current;
        if (!container) {
            return;
        }
        let animation;
        let cancelled = false;
        void (async () => {
            try {
                const [{ default: lottie }, { default: notFoundAnimation }] = await Promise.all([
                    import('lottie-web'),
                    import('./not-found-animation.json'),
                ]);
                if (cancelled) {
                    return;
                }
                animation = lottie.loadAnimation({
                    container,
                    renderer: 'svg',
                    loop: false,
                    autoplay: false,
                    animationData: notFoundAnimation,
                    rendererSettings: { preserveAspectRatio: 'xMidYMin slice' },
                });
                lottieAnimation.current = animation;
            }
            catch (error) {
                if (cancelled) {
                    return;
                }
                // The animation is decorative: the 404 copy and CTA render without it, so a failed
                // chunk load degrades to an empty container rather than breaking the page.
                console.error(new Error('NotFound: failed to load the 404 Lottie animation', { cause: error }));
            }
        })();
        return () => {
            cancelled = true;
            animation?.destroy();
            lottieAnimation.current = null;
        };
    }, []);
    // Run requestAnimationFrame loop, on every RAF frame instruct lottie to animate to the
    // animation frame that corresponds to the current X position of the mouse
    const rafId = React.useRef(0);
    React.useEffect(() => {
        const runFrame = () => {
            const animation = lottieAnimation.current;
            // The animation is loaded asynchronously, so keep scheduling frames until the ref is
            // populated — bailing out here would stop the loop permanently before it ever starts.
            if (animation) {
                // currently the animation is back and forth, so we take half the length as max
                const totalFrames = (animation.totalFrames ?? 0) / 2;
                const videoFrame = Math.floor(totalFrames * relativeXPosition.current);
                animation.goToAndStop(videoFrame, true);
            }
            // set the raf callback for the next frame
            rafId.current = requestAnimationFrame(runFrame);
        };
        // kick start the raf loop
        rafId.current = requestAnimationFrame(runFrame);
        return () => cancelAnimationFrame(rafId.current);
    }, []);
    return (_jsx("div", { className: cn('bg-dep-tertiary', isRebrandPalette && 'bg-static'), children: _jsxs("div", { className: "mx-auto grid w-full max-w-360 grid-flow-dense px-6 pt-0 pb-10 md:min-h-[50vh] md:max-w-437.5 md:grid-cols-[auto_1fr] md:pr-0 md:pb-10 md:pl-10 lg:h-[75vh] xl:pl-25", children: [_jsx("div", { ref: animationContainer, className: "xs:h-full h-75 mix-blend-difference md:order-2 [&_svg]:overflow-visible" }), _jsxs("div", { className: "grid items-center justify-items-start gap-5 self-center", children: [_jsx("h1", { className: cn('text-dep-xl font-dep-display font-normal break-normal wrap-anywhere', isRebrandPalette ? 'text-static' : 'text-dep-inverted'), children: title }), _jsx("p", { className: cn('text-dep-sm font-dep-display font-normal break-normal wrap-anywhere', isRebrandPalette ? 'text-static' : 'text-dep-inverted'), children: body }), renderCta({
                            className: isRebrandPalette
                                ? buttonStyles({ variant: 'pink', shape: 'round' })
                                : cn('text-dep-shade-800 bg-dep-shade-000 border-dep-shade-000 hover:bg-dep-shade-300 hover:text-dep-shade-900 hover:border-dep-shade-100', 'relative inline-flex whitespace-nowrap cursor-pointer justify-center items-center font-dep-mono rounded-full border border-solid px-7 py-3.5 text-[16px]/none focus:focus-ring-dep-thick'),
                            tabIndex: 0,
                        })] })] }) }));
};
